import React, { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useJourneyHref } from '@/administration/pages/Journey/utils/useJourneyNavigate';
import {
  getJourneyTeamsCsvImportStartedMessage,
  JOURNEY_TEAMS_CSV_IMPORT_CONFIRM_ERROR_MESSAGE,
  JOURNEY_TEAMS_CSV_IMPORT_FILE_ERROR_MESSAGE,
  JOURNEY_TEAMS_CSV_IMPORT_FILE_ISSUE_ERROR_CODE,
  JOURNEY_TEAMS_CSV_IMPORT_MAX_ROWS,
  JOURNEY_TEAMS_CSV_IMPORT_SUBTITLE,
  JOURNEY_TEAMS_CSV_IMPORT_TEMPLATE_COLUMNS,
  JOURNEY_TEAMS_CSV_IMPORT_TEMPLATE_FILE_NAME,
  JOURNEY_TEAMS_CSV_IMPORT_TEMPLATE_URL,
  JOURNEY_TEAMS_CSV_IMPORT_TITLE,
} from '@/features/csvImport/admin/constants/journeyTeamsCsvImport';
import { CsvImportUpload } from '@/features/csvImport/admin/organisms/CsvImportUpload';
import { JourneyTeamsCsvImportReview } from '@/features/csvImport/admin/organisms/JourneyTeamsCsvImportReview';
import { CsvImportWizardLayout } from '@/features/csvImport/admin/templates/CsvImportWizardLayout';
import { checkCsvFile } from '@/features/csvImport/admin/utils/checkCsvFile';
import { SharedSnackbarContext, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';
import { RLearningJourneyTeamImportFileErrorCode } from '@/store/v2';
import { LearningJourneyTeamImportPreview } from '@/store/v2/journeys/teamImport';
import { useLearningJourneyTeamImport } from '@/store/v2/journeys/useLearningJourneyTeamImport';
import { Button } from '@/ui/Button';

/**
 * `allJourneyLearnersAssigned` is the server's gate and the authority on coverage — only it can
 * see the journey learners the file never mentions. The row count is the second half of the
 * question: on a journey with no learners at all nobody is missing a team, so the flag is true
 * with nothing to apply, and confirming would build no teams.
 */
const canSetUp = ({ allJourneyLearnersAssigned, stats }: LearningJourneyTeamImportPreview): boolean =>
  allJourneyLearnersAssigned && stats.ready > 0;

/**
 * Every way out of the wizard takes its history entry with it. The file is the only handle on an
 * upload and it lives in component state, so a wizard returned to by Back is a bare Upload step
 * with the success message from the import still standing over it — and a second file dropped
 * there would replace the teams that import had just created.
 */
const REPLACE_WIZARD_ENTRY = { replace: true } as const;

const WIZARD_STEP_COUNT = 2;
const UPLOAD_STEP_INDEX = 0;
const REVIEW_STEP_INDEX = 1;

/** The snackbar drops a plain `\n`, so the messages carry their line break in the markup. */
const SnackbarMessageStyled = styled.span`
  white-space: pre-line;
`;

/**
 * Populating a journey's learning teams from a CSV: upload a file, review how its rows split
 * across the four outcome buckets, then create the teams.
 *
 * The file itself is the only handle on an upload — the preview call returns no id, so confirming
 * re-reads it. That is why the file lives in state across both steps and why the review step is
 * not its own URL: a refresh has nothing to restore from.
 *
 * Unlike the two bulk-invite wizards, set-up is not a background job. The admin waits on the
 * button and lands on the Learning Teams page with the teams already there, so the outcome is
 * reported by a snackbar over that page rather than by an email.
 */
export const JourneyTeamsCsvImportPage = (): JSX.Element => {
  const { journeyId, name } = useParams<{ journeyId: string; name: string }>();
  const navigate = useNavigate();
  const { discussionsHref, discussionsTeamsHref } = useJourneyHref();
  const { openSnackbar, closeSnackbar } = useContext(SharedSnackbarContext);
  const { previewImport, previewLoading, confirmImport, confirmLoading } = useLearningJourneyTeamImport(Number(journeyId));

  const [file, setFile] = useState<File | null>(null);
  const [rowCount, setRowCount] = useState<number | undefined>();
  const [checkingFile, setCheckingFile] = useState(false);
  const [preview, setPreview] = useState<LearningJourneyTeamImportPreview | undefined>();

  const journeyRoute = { journeyId: Number(journeyId), journeyName: name || '' };

  const clearFile = () => {
    setFile(null);
    setRowCount(undefined);
  };

  /**
   * The provider holds one message at a time, so each of these replaces the last rather than
   * stacking — a second rejected file does not leave the first message behind.
   *
   * Errors stand until the admin acts on them. Success goes down on its own: it is raised just
   * before navigating, so a standing one would follow the admin onto unrelated pages.
   */
  const showMessage = (type: SnackbarType, message: string) =>
    openSnackbar({
      type,
      message: <SnackbarMessageStyled>{message}</SnackbarMessageStyled>,
      isDismissive: type === SnackbarType.SUCCESS,
    });

  const showFileError = (code: RLearningJourneyTeamImportFileErrorCode) =>
    showMessage(SnackbarType.DANGER, JOURNEY_TEAMS_CSV_IMPORT_FILE_ERROR_MESSAGE[code]);

  /**
   * The messages stand until something replaces them, so a new file has to take the last one's
   * error down with it — otherwise an accepted file looks like it failed. Rejections from the
   * same drop are raised after this, so their message survives.
   */
  const handleFileSelected = async (selectedFile: File) => {
    closeSnackbar();
    setCheckingFile(true);

    const { issue, rowCount: count } = await checkCsvFile(selectedFile, {
      templateColumns: JOURNEY_TEAMS_CSV_IMPORT_TEMPLATE_COLUMNS,
      maxRows: JOURNEY_TEAMS_CSV_IMPORT_MAX_ROWS,
    });

    setCheckingFile(false);

    // A file that cannot be imported is never staged, so Continue stays disabled over an empty
    // drop area and the admin can drop a replacement straight away.
    if (issue) {
      showFileError(JOURNEY_TEAMS_CSV_IMPORT_FILE_ISSUE_ERROR_CODE[issue]);

      return;
    }

    setFile(selectedFile);
    setRowCount(count);
  };

  const handleContinue = async () => {
    if (!file) return;

    const result = await previewImport(file);

    if (result.errorCode) {
      // Every file-level failure sends the admin back to an empty drop area, since the file that
      // got there cannot be fixed from here.
      clearFile();
      showFileError(result.errorCode);

      return;
    }

    setPreview(result.preview);
  };

  const handleConfirm = async () => {
    if (!file) return;

    const confirmation = await confirmImport(file);

    // Set-up got far enough to fail, so there is nothing to send the admin on to. The design
    // puts them back on Discussion channels rather than on a review of a file that was accepted.
    if (!confirmation) {
      showMessage(SnackbarType.DANGER, JOURNEY_TEAMS_CSV_IMPORT_CONFIRM_ERROR_MESSAGE);
      navigate(discussionsHref(journeyRoute), REPLACE_WIZARD_ENTRY);

      return;
    }

    // Raised before the wizard closes, but the snackbar lives above the router, so it is still
    // there on the Learning Teams page the admin lands on.
    showMessage(SnackbarType.SUCCESS, getJourneyTeamsCsvImportStartedMessage(confirmation.teamsCreated));
    navigate(discussionsTeamsHref(journeyRoute), REPLACE_WIZARD_ENTRY);
  };

  return (
    <CsvImportWizardLayout
      stepCount={WIZARD_STEP_COUNT}
      activeStep={preview ? REVIEW_STEP_INDEX : UPLOAD_STEP_INDEX}
      progressLabel="Set up learning teams progress"
      onBack={preview ? () => setPreview(undefined) : undefined}
      onClose={() => navigate(discussionsHref(journeyRoute), REPLACE_WIZARD_ENTRY)}
      footer={
        preview ? (
          <Button variant="primary" size="large" disabled={!canSetUp(preview)} $loading={confirmLoading} onClick={handleConfirm}>
            Set up
          </Button>
        ) : (
          <Button
            variant="primary"
            size="large"
            disabled={!file}
            $loading={previewLoading || checkingFile}
            onClick={handleContinue}
          >
            Continue
          </Button>
        )
      }
    >
      {preview && file ? (
        <JourneyTeamsCsvImportReview
          fileName={file.name}
          preview={preview}
          onUploadDifferentFile={() => {
            setPreview(undefined);
            clearFile();
          }}
        />
      ) : (
        <CsvImportUpload
          title={JOURNEY_TEAMS_CSV_IMPORT_TITLE}
          subtitle={JOURNEY_TEAMS_CSV_IMPORT_SUBTITLE}
          templateAction={
            <Button
              variant="link"
              icon={['far', 'file-lines']}
              href={JOURNEY_TEAMS_CSV_IMPORT_TEMPLATE_URL}
              download={JOURNEY_TEAMS_CSV_IMPORT_TEMPLATE_FILE_NAME}
            >
              Download template
            </Button>
          }
          file={file}
          rowCount={rowCount}
          loading={previewLoading || checkingFile}
          onFileSelected={handleFileSelected}
          onFileRemoved={clearFile}
          onFilesRejected={() => showFileError(RLearningJourneyTeamImportFileErrorCode.NotCsv)}
        />
      )}
    </CsvImportWizardLayout>
  );
};
